/**
 * PATH: apps/assets/src/App.tsx
 * PURPOSE: Main application component for Assets Hub.
 *
 * WHY: Asset tracking needs the same unified layout and settings surface
 *      as the rest of Quotech for consistent admin control.
 *
 * FLOW:
 *   ┌──────────────┐    ┌──────────────┐    ┌──────────────┐
 *   │ SSO/Session  │───▶│ Layout Shell │───▶│ Assets Routes│
 *   └──────────────┘    └──────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - @quotech/ui: DashboardLayout, buildSettingsTabs
 *   - @quotech/auth: useAuth, ProtectedRoute, SessionManager
 *   - Assets navigation config + pages
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { 
  DashboardLayout, 
  type User, 
  getHubBreadcrumbs,
  buildSettingsTabs,
  useGlobalSearchBridge,
  ErrorBoundary,
  VersionWatcher,
} from '@quotech/ui';
import { useApiClient } from '@quotech/api';
import { useAuth, UnifiedSSOPage, ProtectedRoute, HubGuard, SessionManager, useComputedIsAdmin, useAuthStore, useRBAC } from '@quotech/auth';
import { 
  NAV_ITEMS, 
  MODULE_CONFIG, 
  SECONDARY_NAV_ITEMS, 
  EXTERNAL_LINKS, 
  COMMANDS, 
  HUB_LINKS 
} from './config/navigation';

import DashboardPage from './pages/DashboardPage';
import AssetsPage from './pages/AssetsPage';
import MaintenancePage from './pages/MaintenancePage';
import TrackingPage from './pages/TrackingPage';
import BookingsPage from './pages/BookingsPage';
import ReportsPage from './pages/ReportsPage';
import AlertsPage from './pages/AlertsPage';

/**
 * Main layout wrapper using @quotech/ui
 */
function AssetsLayout() {
  const { user, logout, organisationId, organisationName } = useAuth();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const apiClient = useApiClient();

  // Build breadcrumbs using shared utility from hub registry
  const breadcrumbs = getHubBreadcrumbs('assets', location.pathname);
  
  // Global Search Bridge - provides API client with auth for search to work
  const tokenProvider = useMemo(() => ({
    getAccessToken: () => useAuthStore.getState().accessToken,
    getOrgId: () => useAuthStore.getState().selectedOrg?.id || organisationId,
  }), [organisationId]);
  
  const { apiClient: searchApiClient } = useGlobalSearchBridge({
    tokenProvider,
    onSettingsOpen: useCallback(() => setSettingsOpen(true), []),
  });

  const computedIsAdmin = useComputedIsAdmin(user as Record<string, any> | null);
  const { accessibleModules, permissions: rbacPermissions, hierarchyLevel } = useRBAC();

  // Map auth user to UI user format - includes ALL admin-related fields
  const uiUser: User & { displayEmail?: string } | null = user ? {
    id: user.id,
    name: user.name || user.full_name || user.email,
    email: user.email,
    // Header-only authority display; login identity stays on `email`.
    displayEmail: user.display_email || user.authority_email || user.email,
    organisation: user.organisation_name,
    organisationId: user.organisation_id,
    role: user.role,
    isAdmin: computedIsAdmin,
    admin: (user as Record<string, unknown>).admin as boolean | undefined,
    is_admin: (user as Record<string, unknown>).is_admin as boolean | undefined,
    is_staff: (user as Record<string, unknown>).is_staff as boolean | undefined,
    is_superuser: (user as Record<string, unknown>).is_superuser as boolean | undefined,
  } : null;

  // Handle logout with redirect to master login
  const handleLogout = () => {
    logout();
    window.location.href = 'https://dashboard.quotech.ai/login';
  };

  // Build settings tabs using universal helper
  const settingsTabs = useMemo(() => buildSettingsTabs({
    user: user ? {
      id: user.id,
      first_name: user.first_name || user.name?.split(' ')[0],
      last_name: user.last_name || user.name?.split(' ').slice(1).join(' '),
      email: user.email,
      organisation_name: user.organisation_name || organisationName,
      role: user.role,
      isAdmin: computedIsAdmin,
      permissions: rbacPermissions, hierarchy_level: hierarchyLevel,
    } : null,
    hubId: 'assets',
    hubName: 'Assets Hub',
    apiClient,
    organisationId: organisationId || user?.organisation_id,
    profileData: {
      first_name: user?.first_name || user?.name?.split(' ')[0],
      last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' '),
      email: user?.email,
      organisation_name: user?.organisation_name || organisationName,
    },
    organisationData: {
      id: organisationId || user?.organisation_id || '',
      name: organisationName || user?.organisation_name || 'Organisation',
    },
    integrations: [], // Assets Hub doesn't have direct integrations
  }), [user, organisationId, organisationName, apiClient, computedIsAdmin, rbacPermissions, hierarchyLevel]);
  
  // Update SECONDARY_NAV_ITEMS to use action for settings
  const secondaryNavWithSettingsAction = useMemo(() => 
    SECONDARY_NAV_ITEMS.map(item => 
      item.id === 'settings' 
        ? { ...item, href: undefined, action: () => setSettingsOpen(true) }
        : item
    ), []
  );

  return (
    <DashboardLayout
      navItems={NAV_ITEMS}
      secondaryNavItems={secondaryNavWithSettingsAction}
      externalLinks={EXTERNAL_LINKS}
      module={MODULE_CONFIG}
      currentHub="assets"
      accessibleModules={accessibleModules}
      user={uiUser}
      onLogout={handleLogout}
      breadcrumbs={breadcrumbs}
      showGlobalSearch={true}
      globalSearchApiClient={searchApiClient}
      showCommandPalette
      commands={COMMANDS}
      hubLinks={HUB_LINKS}
      showOrgSwitcher
      showHubSwitcher
      showNotifications
      sidebarSectionLabels={{
        main: 'ASSETS',
        secondary: 'Tools',
        hubs: 'Other Hubs',
      }}
      showSettingsModal
      settingsTabs={settingsTabs}
      settingsModalOpen={settingsOpen}
      onCloseSettings={() => setSettingsOpen(false)}
      onOpenSettings={() => setSettingsOpen(true)}
    >
      <Outlet />
    </DashboardLayout>
  );
}

/**
 * Main App with unified SSO and session management
 */
function App() {
  // Initialize unified session manager
  useEffect(() => {
    const sessionManager = new SessionManager();
    sessionManager.startMonitoring();
    return () => sessionManager.stopMonitoring();
  }, []);

  return (
    <ErrorBoundary>
    {/* Auto-update notifier — see qf!2578 for context (30s poll, 10-min idle auto-reload). */}
    <VersionWatcher pollIntervalMs={30_000} autoReloadAfterMs={null} />
    <Routes>
      {/* Unified SSO Page - processes token from URL */}
      <Route
        path="/sso"
        element={
          <UnifiedSSOPage
            defaultRedirectPath="/assets"
            loadingComponent={
              <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                  <div className="w-10 h-10 border-4 border-slate-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Signing you in to Assets Hub...</p>
                </div>
              </div>
            }
          />
        }
      />

      {/* Protected routes with shared layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute redirectTo="https://dashboard.quotech.ai/login">
            <HubGuard hub="assets"><AssetsLayout /></HubGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/assets" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="maintenance" element={<MaintenancePage />} />
        <Route path="tracking" element={<TrackingPage />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="alerts" element={<AlertsPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/assets" replace />} />
    </Routes>
    </ErrorBoundary>
  );
}

export default App;
